import * as React from 'react';

import type {
  AudienceDelta,
  CohortAudience,
  Learner,
} from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/types';
import { AUDIENCE_LEARNERS_PAGE_SIZE } from 'bundles/enterprise-admin-messaging-v2/hooks/useAudienceLearners';
import { useCohortLearners } from 'bundles/enterprise-admin-messaging-v2/hooks/useCohortLearners';
import type { LearnerRow } from 'bundles/enterprise-admin-messaging-v2/hooks/useLearnerList';

const SEARCH_DEBOUNCE_MS = 300;

type Params = {
  cohortAudience?: CohortAudience;
  /** Delta from the edit screen to overlay on the cohort results. */
  delta?: AudienceDelta;
  /** Full Learner objects for `delta.addedIds` (needed to render name/email). */
  addedLearners?: Learner[];
};

type Result = {
  searchQuery: string;
  setSearchQuery: (next: string) => void;
  page: number;
  setPage: (next: number) => void;
  pageCount: number;
  pageRows: LearnerRow[];
  filteredCount: number;
  loading: boolean;
  error: boolean;
};

/**
 * Matches an added learner against the current search query (client-side).
 * Used to filter the prepended additions on the View screen when searching.
 */
const matchesSearch = (learner: Learner, query: string): boolean => {
  if (!query) return true;
  const q = query.toLowerCase();
  return learner.name.toLowerCase().includes(q) || learner.email.toLowerCase().includes(q);
};

/**
 * Server-backed learner list for the NLQ cohort flow. Uses
 * `fetchEnterpriseLearnerCohorts` for pagination and server-side search via
 * `searchString`. When a `delta` is provided (from the Edit learner list
 * screen):
 *  - Removals are filtered out in-place (pages may show fewer than 7 rows).
 *  - Additions appear after the last cohort page, filling the tail naturally.
 *    If additions overflow the last page, they spill onto virtual pages that
 *    are paginated client-side without further cohort API calls.
 */
export const useCohortLearnerList = ({ cohortAudience, delta, addedLearners }: Params): Result => {
  const [searchQuery, setSearchQueryRaw] = React.useState('');
  const [debouncedSearch, setDebouncedSearch] = React.useState('');
  const [page, setPage] = React.useState(1);

  React.useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const removedIdsKey = delta?.removedIds?.join(',') ?? '';
  const removedSet = React.useMemo(
    () => new Set(delta?.removedIds ?? []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [removedIdsKey]
  );

  const visibleAdditions = React.useMemo(() => {
    if (!addedLearners?.length) return [];
    return addedLearners.filter((l) => matchesSearch(l, debouncedSearch));
  }, [addedLearners, debouncedSearch]);

  const additionsCount = visibleAdditions.length;

  // Track the last cohort page number and how many filtered rows it had.
  // These are stored so virtual pages (which skip the cohort fetch) can
  // correctly compute the additions offset without stale query data.
  const [lastCohortPage, setLastCohortPage] = React.useState<number | null>(null);
  const [lastCohortPageRowCount, setLastCohortPageRowCount] = React.useState(0);
  const [snapshotCohortTotal, setSnapshotCohortTotal] = React.useState(0);

  const isVirtualPage = lastCohortPage !== null && page > lastCohortPage;

  const {
    learners: rawLearners,
    hasMore,
    loading,
    error,
  } = useCohortLearners({
    cohortAudience,
    page: isVirtualPage ? lastCohortPage! : page,
    pageSize: AUDIENCE_LEARNERS_PAGE_SIZE,
    search: debouncedSearch,
    skip: isVirtualPage,
  });

  // Filter out removed learners from cohort results.
  const filteredCohortLearners = React.useMemo(
    () => rawLearners.filter((l) => !removedSet.has(l.id)),
    [rawLearners, removedSet]
  );

  // Snapshot the last cohort page and its row count when we detect end-of-data.
  React.useEffect(() => {
    if (!hasMore && !loading && !isVirtualPage && cohortAudience) {
      setLastCohortPage(page);
      setLastCohortPageRowCount(filteredCohortLearners.length);
      setSnapshotCohortTotal((page - 1) * AUDIENCE_LEARNERS_PAGE_SIZE + rawLearners.length);
    }
  }, [hasMore, loading, isVirtualPage, page, cohortAudience, filteredCohortLearners.length, rawLearners.length]);

  // Reset when search changes (cohort size changes).
  React.useEffect(() => {
    setLastCohortPage(null);
    setLastCohortPageRowCount(0);
    setSnapshotCohortTotal(0);
  }, [debouncedSearch]);

  // How many addition slots were available on the last cohort page.
  const slotsForAdditionsOnLastPage = Math.max(0, AUDIENCE_LEARNERS_PAGE_SIZE - lastCohortPageRowCount);
  const additionsShownOnLastPage = Math.min(additionsCount, slotsForAdditionsOnLastPage);

  // Build page rows.
  const pageRows: LearnerRow[] = React.useMemo(() => {
    if (isVirtualPage) {
      // Virtual page: only additions, offset past what was shown on the last cohort page.
      const virtualPageIndex = page - lastCohortPage! - 1;
      const start = additionsShownOnLastPage + virtualPageIndex * AUDIENCE_LEARNERS_PAGE_SIZE;
      const end = start + AUDIENCE_LEARNERS_PAGE_SIZE;
      return visibleAdditions.slice(start, end).map((learner) => ({ learner, inList: true }));
    }

    const isLastCohortPage = !hasMore && !loading;
    const cohortRows: LearnerRow[] = filteredCohortLearners.map((learner) => ({ learner, inList: true }));

    if (isLastCohortPage && additionsCount > 0) {
      const additionRows: LearnerRow[] = visibleAdditions.map((learner) => ({ learner, inList: true }));
      return [...cohortRows, ...additionRows].slice(0, AUDIENCE_LEARNERS_PAGE_SIZE);
    }

    return cohortRows;
  }, [
    isVirtualPage,
    hasMore,
    loading,
    filteredCohortLearners,
    visibleAdditions,
    additionsCount,
    page,
    lastCohortPage,
    additionsShownOnLastPage,
  ]);

  // Total count estimate adjusted for delta.
  const removedCount = delta?.removedIds?.length ?? 0;
  const baseCohortCount = isVirtualPage
    ? snapshotCohortTotal
    : hasMore
    ? (page + 1) * AUDIENCE_LEARNERS_PAGE_SIZE
    : (page - 1) * AUDIENCE_LEARNERS_PAGE_SIZE + rawLearners.length;
  const filteredCount = Math.max(0, baseCohortCount - removedCount + additionsCount);

  // Page count: cohort pages + virtual pages for overflow additions.
  const overflowAdditions = Math.max(0, additionsCount - additionsShownOnLastPage);
  const virtualPageCount = Math.ceil(overflowAdditions / AUDIENCE_LEARNERS_PAGE_SIZE);

  const pageCount = React.useMemo(() => {
    if (isVirtualPage && lastCohortPage !== null) {
      return lastCohortPage + virtualPageCount;
    }
    if (hasMore || loading) {
      return page + 1;
    }
    return page + virtualPageCount;
  }, [isVirtualPage, lastCohortPage, hasMore, loading, page, virtualPageCount]);

  const setSearchQuery = React.useCallback((next: string) => {
    setSearchQueryRaw(next);
    setPage(1);
  }, []);

  return {
    searchQuery,
    setSearchQuery,
    page,
    setPage,
    pageCount,
    pageRows,
    filteredCount,
    loading: loading && !isVirtualPage,
    error,
  };
};

export default useCohortLearnerList;
